import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import { MDBIcon, MDBAnimation, MDBProgress } from "mdbreact";
import { useToasts } from "react-toast-notifications";
import { BROWSE as MENUS } from "../../../services/redux/slices/diagnostics/clinic/clinicMenus";
import { currency, globalSearch } from "../../../services/utilities";
import Notification from "./notifications";
import "../style.css";

/**
 * A Search component that allows the user to search for a menu item by name.
 * The component will make an API call to search for menu items and render a list of results below the search input.
 * The user can select a menu item from the list and the setMenu callback will be called with the selected menu item.
 * The component also renders a button to register a new menu item if no menu item record is found with the search key.
 * The setRegister callback will be called with the search key when the button is clicked.
 *
 * @param {function} setMenu - A callback function that will be called when a menu item is selected from the list.
 * @param {function} setRegister - A callback function that will be called when the button to register a new menu item is clicked.
 *
 * @returns {JSX.Element} user
 */
export default function Search({
  setMenu,
  setRegister = () => {},
  clinic = "",
  physicianId = "",
}) {
  const { token, activePlatform } = useSelector(({ auth }) => auth),
    { collections } = useSelector(({ clinicMenus }) => clinicMenus),
    [match, setMatch] = useState([]),
    [searchKey, setSearchKey] = useState(""),
    [isLoading, setIsLoading] = useState(false),
    { addToast } = useToasts(),
    dispatch = useDispatch();

  const inputRef = useRef(null); // Reference to the input field
  const normalizedClinicId = String(clinic || "");
  const normalizedPhysicianId = String(physicianId || "");
  // initial values
  useEffect(() => {
    if (token && activePlatform) {
      const { branch = {} } = activePlatform;
      const { physicians = [] } = branch || {};
      const physicianIDS = normalizedPhysicianId
        ? [normalizedPhysicianId]
        : physicians.map(({ _id }) => _id).filter(Boolean) || [];
      if (!physicianIDS.length || collections.length > 0) return;

      dispatch(MENUS({ token, key: { physicianId: physicianIDS } })).catch(
        (error) => {
          console.error("Error fetching menus:", error);
        },
      );
    }
  }, [
    token,
    dispatch,
    activePlatform,
    collections.length,
    normalizedPhysicianId,
  ]);

  // Debounced search function to avoid too many re-renders
  const debouncedSearch = useMemo(
    () =>
      debounce((key) => {
        if (key.trim().length <= 1) {
          setIsLoading(false);
          return setMatch([]);
        }
        const scopedCollections = collections.filter((menu) => {
          const menuClinicId = String(
            menu?.clinicId || menu?.clinic?._id || menu?.clinic || "",
          );
          const menuPhysicianId = String(
            menu?.physician?._id || menu?.physician || "",
          );

          const isClinicMatch =
            !normalizedClinicId || menuClinicId === normalizedClinicId;
          const isPhysicianMatch =
            !normalizedPhysicianId || menuPhysicianId === normalizedPhysicianId;

          return isClinicMatch && isPhysicianMatch;
        });
        const _match = globalSearch(scopedCollections, key.trim());
        setIsLoading(false);
        setMatch(_match);
      }, 800),
    [collections, normalizedClinicId, normalizedPhysicianId]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
      setIsLoading(false);
    };
  }, [debouncedSearch]);

  const handleChange = (value) => {
    setSearchKey(value);
    setIsLoading(true);
    // Debounced search trigger
    debouncedSearch(value);
  };

  const handleSelect = (menu) => {
    setMenu(menu);
    setSearchKey(""); // Reset the search key after selection
    setMatch([]); // Clear the match list after selection
    inputRef.current.focus(); // Keep focus on the input field after selection
  };

  const handleRegister = () => {
    setRegister(searchKey);
    setSearchKey(""); // Reset the search key after registration
    setMatch([]); // Clear the match list after registration
    inputRef.current.focus(); // Keep focus on the input field after registration
  };

  const handleClear = () => {
    setSearchKey(""); // Clear the search key
    setMatch([]); // Clear the match list
  };

  return (
    <div className="d-flex align-items-center " style={{ width: "100%" }}>
      <Notification didSearch={match.length > 0} />
      <div className={`searchable-search  ${searchKey && "active"}`}>
        <div className="searchable-search-suggestions">
          {!isLoading ? (
            <>
              {searchKey && match.length === 0 && (
                <li onClick={handleRegister} className="text-dark">
                  No services found for "{searchKey}". try another keywords
                </li>
              )}
              {match.length === 0 && !searchKey && (
                <li>Please type a menu name.</li>
              )}
              {match.length > 0 &&
                match.map((menu, index) => {
                  const {
                    description = "",
                    abbreviation = "",
                    srp: opd = 0,
                  } = menu;

                  return (
                    <li
                      style={{ listStyle: "none" }}
                      key={`menu-suggestion-${index}`}
                      onClick={() => {
                        if (opd) {
                          handleSelect(menu);
                        } else {
                          addToast("This product has no set price.", {
                            appearance: "warning",
                          });
                        }
                      }}
                    >
                      <div className="d-flex align-items-left justify-content-between menu-suggestion p-2">
                        <span className="text-left  ">
                          {description && (
                            <span className="description text-dark">
                              {description.toLowerCase().toUpperCase()}
                            </span>
                          )}
                        </span>
                        <span className="ml-3 text-dark d-flex align-items-center">
                          <span className="mr-2">{abbreviation}</span>
                          <span>{currency.format(Number(opd || 0))}</span>
                        </span>
                      </div>
                    </li>
                  );
                })}
            </>
          ) : (
            <>
              {new Array(5).fill("").map((_, index) => (
                <MDBAnimation
                  key={index}
                  className="p-1 ml-2 mr-2 "
                  type="flash"
                  infinite
                  delay={`${index + 1}00ms`}
                  duration="3000ms"
                >
                  <MDBProgress
                    color="light"
                    value={3000}
                    id="progress-table"
                  ></MDBProgress>
                </MDBAnimation>
              ))}
            </>
          )}
        </div>
        <input
          ref={inputRef} // Attach the reference to the input element
          placeholder="Search menus..."
          value={searchKey}
          onChange={({ target }) => handleChange(target.value)}
          autoCorrect="off"
          spellCheck={false}
        />
        <button
          type="submit"
          className={`${match.length > 0 && "bg-danger"}`}
          onClick={handleClear}
        >
          <MDBIcon
            icon={match.length > 0 ? "times" : "search"}
            className="search-icon"
          />
        </button>
      </div>
    </div>
  );
}
